import React, { useState } from 'react';
import Reaptcha from 'reaptcha';
import { Field, Form, Formik } from 'formik';

import { Button, Col, Row } from 'react-bootstrap';

import { BidSchema } from 'schema/bid';
import { propertyUseOptions } from 'schema/property-use';

import BidDisclaimer from 'components/bid-disclaimer/bid-disclaimer.component';

import { FormCheckbox } from 'ui-kit/checkbox/checkbox';
import FormSectionLabel from 'ui-kit/form-section-label/form-section-label';
import Select from 'ui-kit/select/select';
import Text from 'ui-kit/text/text';

import './style.css';

const plannedImprovementsCostOptions = [
    {
        key: 'true',
        label: 'Yes',
        value: true
    },
    {
        key: 'false',
        label: 'No',
        value: false
    }
];

const BidForm = ({ currentHighBid, startingBid, bid, onSubmit, readOnly }) => {
    const [captchaToken, setCaptchaToken] = useState('');
    const [captchaRef, setCaptchaRef] = useState(undefined);
    const [captchaVerified, setCaptchaVerified] = useState(false);

    const handleSubmit = (values) => {
        if (captchaVerified && onSubmit) {
            captchaRef.reset();

            onSubmit({ captchaToken, ...values });
        }
    };

    const onVerify = (verifyToken) => {
        setCaptchaVerified(true);
        setCaptchaToken(verifyToken);
    };

    return (
        <div className="bid-form">
            {currentHighBid && (
                <Row>
                    <Col>
                        <FormSectionLabel label={`Current High Bid: $${currentHighBid.amount}`} />
                    </Col>
                </Row>
            )}
            <BidDisclaimer />
            <Formik
                onSubmit={handleSubmit}
                validationSchema={BidSchema}
                initialValues={{
                    acceptedClosingCosts: bid?.acceptedClosingCosts,
                    address: bid?.address,
                    homeAddress: bid?.homeAddress,
                    parcelNumber: bid?.parcelNumber,
                    name: bid?.name,
                    company: bid?.company || '',
                    orgOrCompanyControlling: bid?.orgOrCompanyControlling || '',
                    propertyUse: bid?.propertyUse,
                    plannedImprovements: bid?.plannedImprovements,
                    timeline: bid?.timeline,
                    improvementCosts: bid?.improvementCosts,
                    fundingMethod: bid?.fundingMethod,
                    communitySupport: bid?.communitySupport,
                    delinquentPropertyTaxes: bid?.delinquentPropertyTaxes,
                    additionalRealEstateOwnership: bid?.additionalRealEstateOwnership,
                    plannedImprovementsRequiresFinancingDocumentation:
                        bid?.plannedImprovementsRequiresFinancingDocumentation,
                    amount: bid?.amount,
                    email: bid?.email
                }}
            >
                {(formik) => (
                    <Form onSubmit={formik.handleSubmit}>
                        <Row>
                            <Col>
                                <FormSectionLabel label="Property Information" />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} md={6}>
                                <Text
                                    name="address"
                                    label="Property Address"
                                    onChange={formik.handleChange}
                                    value={formik.values?.address}
                                    defaultValue={formik.values?.address}
                                    touched={formik.touched.address}
                                    errors={formik.errors?.address}
                                    disabled
                                />
                            </Col>
                            <Col xs={12} md={6}>
                                <Text
                                    name="parcelNumber"
                                    label="Parcel Number"
                                    onChange={formik.handleChange}
                                    value={formik.values?.parcelNumber}
                                    defaultValue={formik.values?.parcelNumber}
                                    touched={formik.touched.parcelNumber}
                                    errors={formik.errors?.parcelNumber}
                                    disabled
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormSectionLabel label="Contact Information" />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Text
                                    name="name"
                                    label="Name"
                                    onChange={formik.handleChange}
                                    value={formik.values?.name}
                                    defaultValue={formik.values?.name}
                                    touched={formik.touched.name}
                                    errors={formik.errors?.name}
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Text
                                    name="homeAddress"
                                    label="Home Address"
                                    onChange={formik.handleChange}
                                    value={formik.values?.homeAddress}
                                    defaultValue={formik.values?.homeAddress}
                                    touched={formik.touched.homeAddress}
                                    errors={formik.errors?.homeAddress}
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Text
                                    name="company"
                                    label="Company"
                                    onChange={formik.handleChange}
                                    value={formik.values?.company}
                                    defaultValue={formik.values?.company}
                                    touched={formik.touched.company}
                                    errors={formik.errors?.company}
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Text
                                    name="orgOrCompanyControlling"
                                    label="Organization or Company (with controlling interest)"
                                    onChange={formik.handleChange}
                                    value={formik.values?.orgOrCompanyControlling}
                                    defaultValue={formik.values?.orgOrCompanyControlling}
                                    touched={formik.touched.orgOrCompanyControlling}
                                    errors={formik.errors?.orgOrCompanyControlling}
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Text
                                    name="email"
                                    label="Email Address"
                                    onChange={formik.handleChange}
                                    value={formik.values?.email}
                                    defaultValue={formik.values?.email}
                                    touched={formik.touched.email}
                                    errors={formik.errors?.email}
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <FormSectionLabel label="Intended Use For This Property" />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Select
                                    id="bid-form-property-use-select"
                                    name="propertyUse"
                                    className="mb-4"
                                    options={propertyUseOptions}
                                    placeholder="Property use"
                                    formOnChange={formik.handleChange}
                                    defaultValue={formik.values?.propertyUse}
                                    value={formik.values?.propertyUse}
                                    errors={formik.errors?.propertyUse}
                                    touched={formik.touched.propertyUse}
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Text
                                    name="plannedImprovements"
                                    label="Description of planned improvements"
                                    onChange={formik.handleChange}
                                    value={formik.values?.plannedImprovements}
                                    defaultValue={formik.values?.plannedImprovements}
                                    touched={formik.touched.plannedImprovements}
                                    errors={formik.errors?.plannedImprovements}
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Text
                                    name="timeline"
                                    label="Timeline for improvements"
                                    onChange={formik.handleChange}
                                    value={formik.values?.timeline}
                                    defaultValue={formik.values?.timeline}
                                    touched={formik.touched.timeline}
                                    errors={formik.errors?.timeline}
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Text
                                    name="improvementCosts"
                                    label="Total Projected Cost of Improvements"
                                    onChange={formik.handleChange}
                                    value={formik.values?.improvementCosts}
                                    defaultValue={formik.values?.improvementCosts}
                                    touched={formik.touched.improvementCosts}
                                    errors={formik.errors?.improvementCosts}
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Text
                                    name="fundingMethod"
                                    label="How will the purchase and improvements be funded"
                                    onChange={formik.handleChange}
                                    value={formik.values?.fundingMethod}
                                    defaultValue={formik.values?.fundingMethod}
                                    touched={formik.touched.fundingMethod}
                                    errors={formik.errors?.fundingMethod}
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Text
                                    name="communitySupport"
                                    label="Community Support"
                                    onChange={formik.handleChange}
                                    value={formik.values?.communitySupport}
                                    defaultValue={formik.values?.communitySupport}
                                    touched={formik.touched.communitySupport}
                                    errors={formik.errors?.communitySupport}
                                    tooltipText="Do you currently have the support of any neighborhood/community groups for this proposed use? If
                                    so list them."
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Text
                                    name="delinquentPropertyTaxes"
                                    label="Delinquent Property Taxes"
                                    onChange={formik.handleChange}
                                    value={formik.values?.delinquentPropertyTaxes}
                                    defaultValue={formik.values?.delinquentPropertyTaxes}
                                    touched={formik.touched.delinquentPropertyTaxes}
                                    errors={formik.errors?.delinquentPropertyTaxes}
                                    tooltipText="Do you have any delinquent property taxes or outstanding code violations on any property that
                                    you own? If yes, please explain."
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Text
                                    name="additionalRealEstateOwnership"
                                    label="List addresses of other properties you own"
                                    onChange={formik.handleChange}
                                    value={formik.values?.additionalRealEstateOwnership}
                                    defaultValue={formik.values?.additionalRealEstateOwnership}
                                    touched={formik.touched.additionalRealEstateOwnership}
                                    errors={formik.errors?.additionalRealEstateOwnership}
                                    tooltipText="Do you own multiple parcels of real estate in Delaware County? If so, please list all addresses,
                                    including those owned by companies that you control. You may attach additional pages if
                                    necessary."
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12}>
                                <Select
                                    id="bid-form-planned-improvements-requires-financing-documentation"
                                    name="plannedImprovementsRequiresFinancingDocumentation"
                                    className="mb-4"
                                    options={plannedImprovementsCostOptions}
                                    placeholder="Will the planned improvements cost $10,000 or more?"
                                    formOnChange={formik.handleChange}
                                    defaultValue={formik.values?.plannedImprovementsRequiresFinancingDocumentation}
                                    value={formik.values?.plannedImprovementsRequiresFinancingDocumentation}
                                    errors={formik.errors?.plannedImprovementsRequiresFinancingDocumentation}
                                    touched={formik.touched.plannedImprovementsRequiresFinancingDocumentation}
                                    disabled={readOnly}
                                />
                            </Col>
                            {formik.values?.plannedImprovementsRequiresFinancingDocumentation && (
                                <Col xs={12}>
                                    <div className="px-3">
                                        <p>
                                            <strong>
                                                Due to planned improvements exceeding $10,000, one or more of the
                                                following items will be required to be submitted to the
                                            </strong>{' '}
                                            <a href="mailto:mlb@muncielandbank.org">Muncie Land Bank:</a>
                                        </p>
                                        <ul>
                                            <li>Pre-qualification letter from lender (if applicable)</li>
                                            <li>Personal financial statement</li>
                                            <li>Your most recent tax return</li>
                                            <li>Copy of a photo identification</li>
                                            <li>Proof of social security number</li>
                                            <li>Most recent pay stub</li>
                                            <li>
                                                A letter explaining how you will complete the project using your own
                                                labor
                                            </li>
                                        </ul>
                                    </div>
                                </Col>
                            )}
                        </Row>
                        <Row>
                            <Col>
                                <Text
                                    name="amount"
                                    label={`Bid Amount (minimum bid: $${
                                        currentHighBid ? currentHighBid.amount : startingBid
                                    })`}
                                    onChange={formik.handleChange}
                                    value={formik.values?.amount}
                                    defaultValue={formik.values?.amount}
                                    touched={formik.touched.amount}
                                    errors={formik.errors?.amount}
                                    disabled={readOnly}
                                />
                            </Col>
                        </Row>
                        {!readOnly && (
                            <>
                                <Row>
                                    <Col>
                                        <Field
                                            name="acceptedClosingCosts"
                                            component={FormCheckbox}
                                            label="I acknowledge that the bidder is responsible for closing costs (approx. 200$ with no title insurance)."
                                            onChange={formik.handleChange}
                                            errors={formik.errors?.acceptedClosingCosts}
                                            touched={formik.touched.acceptedClosingCosts}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="mb-3"
                                            disabled={captchaVerified ? '' : 'disabled'}
                                        >
                                            Submit
                                        </Button>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Reaptcha
                                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                            onVerify={onVerify}
                                            ref={(e) => setCaptchaRef(e)}
                                        />
                                    </Col>
                                </Row>
                            </>
                        )}
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default BidForm;
